<template>
  <FormGroup>
    <RadioButtonGroup>
      <RadioButton v-model="form.fields.allowsPets" name="allowsPets" :value="true">Pets allowed</RadioButton>
      <RadioButton v-model="form.fields.allowsPets" name="allowsPets" :value="false">Pets Not allowed</RadioButton>
    </RadioButtonGroup>

    <FormGroup v-if="form.fields.allowsPets">
      <Divider />
      <Text variant="h4">Pets allowed in the property</Text>

      <FormGroup gap="medium" class="checkboxes">
        <FormField
          label="Large dogs"
          direction="row"
          label-position="after"
          :error="form.fieldErrors('largeDogsPolicy')"
        >
          <Checkbox v-model="form.fields.largeDogsPolicy" name="largeDogsPolicy" />
        </FormField>

        <FormField
          label="Small dogs"
          direction="row"
          label-position="after"
          :error="form.fieldErrors('smallDogsPolicy')"
        >
          <Checkbox v-model="form.fields.smallDogsPolicy" name="smallDogsPolicy" />
        </FormField>

        <FormField label="Cats" direction="row" label-position="after" :error="form.fieldErrors('catsPolicy')">
          <Checkbox v-model="form.fields.catsPolicy" name="catsPolicy" />
        </FormField>

        <FormField label="Other" direction="row" label-position="after" :error="form.fieldErrors('otherPetsPolicy')">
          <Checkbox v-model="form.fields.otherPetsPolicy" name="otherPetsPolicy" />
        </FormField>
      </FormGroup>

      <FormField label="Max number of pets" direction="row" :error="form.fieldErrors('maxPets')">
        <InputStepper v-model="form.fields.maxPets" name="maxPets" />
      </FormField>

      <Text variant="h4">Pet fees</Text>
      <FormGroup>
        <FormField label="Pet rent (if applicable):" :error="form.fieldErrors('petRent')">
          <Input v-model="form.fields.petRent" type="number">
            <template #prefix>$</template>
          </Input>
        </FormField>

        <FormField label="Pet deposit (if applicable):" :error="form.fieldErrors('petDeposit')">
          <Input v-model="form.fields.petDeposit" type="number">
            <template #prefix>$</template>
          </Input>
        </FormField>

        <FormField label="One-time pet fee (if applicable):" :error="form.fieldErrors('petFee')">
          <Input v-model="form.fields.petFee" type="number">
            <template #prefix>$</template>
          </Input>
        </FormField>
      </FormGroup>
    </FormGroup>
  </FormGroup>
</template>

<script setup lang="ts">
import { watch } from 'vue';

import {
  Checkbox,
  RadioButton,
  InputStepper,
  Input,
  FormField,
  FormGroup,
  RadioButtonGroup,
  Divider,
  useForm,
  Text,
} from '@tallo/design-system';
import { PetPolicyDto, PetPolicy } from '@tallo/property';

import { useDetailsHolder } from '../../composables/details-holder';
import { SavePetPolicyDto } from '../../dtos/save-pet-policy.dto.interface';

type PetPolicyField = 'catsPolicy' | 'largeDogsPolicy' | 'smallDogsPolicy' | 'otherPetsPolicy';

interface PropertyDetailsPetPolicyForm extends Omit<SavePetPolicyDto, PetPolicyField> {
  catsPolicy: boolean;
  largeDogsPolicy: boolean;
  smallDogsPolicy: boolean;
  otherPetsPolicy: boolean;
}

const holder = useDetailsHolder();
const defaultMaxPets = 3;
const form = useForm<PropertyDetailsPetPolicyForm>({
  allowsPets: true,
  catsPolicy: true,
  largeDogsPolicy: true,
  smallDogsPolicy: true,
  otherPetsPolicy: true,
  petDeposit: null,
  petFee: null,
  petRent: null,
  maxPets: defaultMaxPets,
});

function isAllowed(petPolicy: PetPolicyDto | undefined, key: PetPolicyField) {
  return petPolicy && Object.prototype.hasOwnProperty.call(petPolicy, key)
    ? petPolicy[key] === PetPolicy.Allowed
    : true;
}

function convertToEnum(value: boolean) {
  return value ? PetPolicy.Allowed : PetPolicy.NotAllowed;
}

function populateForm(petPolicy?: PetPolicyDto) {
  Object.assign(form.fields, {
    allowsPets: petPolicy?.allowsPets ?? true,
    catsPolicy: isAllowed(petPolicy, 'catsPolicy'),
    largeDogsPolicy: isAllowed(petPolicy, 'largeDogsPolicy'),
    smallDogsPolicy: isAllowed(petPolicy, 'smallDogsPolicy'),
    otherPetsPolicy: isAllowed(petPolicy, 'otherPetsPolicy'),
    petDeposit: petPolicy?.petDeposit || null,
    petFee: petPolicy?.petFee || null,
    petRent: petPolicy?.petRent || null,
    maxPets:
      petPolicy && Object.prototype.hasOwnProperty.call(petPolicy, 'maxPets')
        ? petPolicy?.maxPets || 0
        : defaultMaxPets,
  });
}

async function save() {
  return form.submit(async (fields) => {
    const payload: SavePetPolicyDto = {
      ...fields,
      catsPolicy: convertToEnum(fields.catsPolicy),
      largeDogsPolicy: convertToEnum(fields.largeDogsPolicy),
      smallDogsPolicy: convertToEnum(fields.smallDogsPolicy),
      otherPetsPolicy: convertToEnum(fields.otherPetsPolicy),
    };

    return await holder.savePetPolicy(payload);
  });
}

// By the row's id, not the row: the building page re-reads its building in the background, and a
// fresh copy of the same policy must not wipe what is being typed into the open sheet
watch(
  () => holder.details?.petPolicy?.id,
  () => populateForm(holder.details?.petPolicy ?? undefined),
  { immediate: true },
);
watch(
  () => form.fields.allowsPets,
  (newValue, oldValue) => {
    if (!oldValue && newValue) {
      form.fields.catsPolicy = true;
      form.fields.largeDogsPolicy = true;
      form.fields.smallDogsPolicy = true;
      form.fields.otherPetsPolicy = true;
      form.fields.maxPets = defaultMaxPets;
    }
  },
);

defineExpose({ save, form });
</script>
